import React from 'react';
import { User, Code, Zap, Target, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const About = ({ data }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const highlights = [
    {
      icon: <Code className="h-6 w-6" />,
      title: "Technical Excellence",
      description: data.expertise
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Experience Range",
      description: data.experience
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Product Mindset",
      description: data.mindset
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="about" className="py-24 px-4 sm:px-6 lg:px-8" ref={ref}>
      <motion.div 
        className="max-w-6xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <div className="text-center mb-20">
          <h2 className="text-5xl sm:text-6xl font-bold text-white mb-4 tracking-tight">
            About Me
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-400 mx-auto mb-6" />
        </div>

        {/* Main Summary */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <p className="text-xl text-gray-300 leading-relaxed">
            {data.summary}
          </p>
        </div>

        <motion.div 
          className="grid lg:grid-cols-3 gap-8"
          variants={itemVariants}
        >
          {/* Background Details */}
          <motion.div
            className="lg:col-span-2"
          >
            <Card className="bg-gray-900/40 border border-gray-800/50 hover:border-gray-700/80 transition-all duration-300 h-full hover:shadow-lg hover:shadow-emerald-500/5">
              <CardContent className="p-6">
                <motion.div 
                  className="flex items-center mb-4"
                  initial={{ x: -20, opacity: 0 }}
                  animate={inView ? { x: 0, opacity: 1 } : { x: -20, opacity: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.2 }}
                    transition={{ duration: 0.5 }}
                  >
                    <User className="h-6 w-6 text-emerald-400 mr-3" />
                  </motion.div>
                  <h3 className="text-xl font-mono font-bold text-gray-100">Background</h3>
                </motion.div>
                
                <motion.div 
                  className="space-y-4"
                  variants={{
                    hidden: {},
                    visible: {
                      transition: {
                        staggerChildren: 0.2,
                        delayChildren: 1
                      }
                    }
                  }}
                  initial="hidden"
                  animate={inView ? "visible" : "hidden"}
                >
                  <motion.div variants={itemVariants}>
                    <h4 className="text-emerald-400 font-semibold text-base mb-2">Journey</h4>
                    <p className="text-gray-300 text-base leading-relaxed">{data.experience}</p>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <h4 className="text-emerald-400 font-semibold text-base mb-2">Core Skills</h4>
                    <p className="text-gray-300 text-base leading-relaxed">{data.expertise}</p>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <h4 className="text-emerald-400 font-semibold text-base mb-2">Philosophy</h4>
                    <p className="text-gray-300 font-mono text-sm italic leading-relaxed">{data.mindset}</p>
                  </motion.div>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Key Metrics */}
          <motion.div>
            <Card className="bg-gray-900/40 border border-gray-800/50 hover:border-gray-700/80 transition-all duration-300 h-full hover:shadow-lg hover:shadow-emerald-500/5">
              <CardContent className="p-6">
                <motion.div 
                  className="flex items-center mb-4"
                  initial={{ x: 20, opacity: 0 }}
                  animate={inView ? { x: 0, opacity: 1 } : { x: 20, opacity: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <motion.div
                    whileHover={{ scale: 1.3, rotate: 180 }}
                    transition={{ duration: 0.4 }}
                  >
                    <TrendingUp className="h-6 w-6 text-emerald-400 mr-3" />
                  </motion.div>
                  <h3 className="text-xl font-mono font-bold text-gray-100">Impact</h3>
                </motion.div>
                <motion.div 
                  className="space-y-3"
                  variants={{
                    hidden: {},
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                        delayChildren: 1.2
                      }
                    }
                  }}
                  initial="hidden"
                  animate={inView ? "visible" : "hidden"}
                >
                  {data.metrics.map((metric, index) => (
                    <motion.div 
                      key={index}
                      variants={{
                        hidden: { x: 20, opacity: 0, scale: 0.9 },
                        visible: { 
                          x: 0, 
                          opacity: 1, 
                          scale: 1,
                          transition: { duration: 0.5 }
                        }
                      }}
                      whileHover={{ 
                        scale: 1.05,
                        backgroundColor: "rgba(16, 185, 129, 0.1)",
                        borderColor: "rgba(16, 185, 129, 0.3)"
                      }}
                      className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 cursor-default"
                    >
                      <p className="text-sm text-gray-300 font-mono">{metric}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default About;